import React, { useState, useEffect } from 'react';
import {
  Mail,
  MapPin,
  Globe,
  Share2,
  Phone,
  ArrowUp,
  Link2,
  ShieldCheck,
  Lock,
  Zap,
  Activity,
  HelpCircle,
  BookOpen,
  Wrench,
  Play,
  ArrowRight
} from 'lucide-react';
import BrandLogo from './BrandLogo';

const Footer = ({ setActiveTab }) => {
  const currentYear = new Date().getFullYear();
  const [copied, setCopied] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLinkClick = (id) => {
    if (id === 'manpower-sourcing') {
      window.open(`${window.location.origin}${window.location.pathname}?tab=manpower-sourcing`, '_blank');
      return;
    }
    if (setActiveTab) setActiveTab(id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <footer id="site-footer" className="relative text-white pt-14 pb-8 overflow-hidden" style={{ background: 'var(--footer-bg, #0f172a)' }}>
      {/* Top Accent Gradient Border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-cyan-400 to-blue-600"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        

        {/* ─── MAIN FOOTER GRID ─── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12 mb-12">

          {/* Brand Column */}
          <div className="space-y-5">
            <BrandLogo isDark={true} onClick={() => handleLinkClick('home')} />

            <p className="text-sm text-gray-400 leading-relaxed">
              Empowering engineers & enterprises with high-performance Remote Hardware & Software Simulation environments.
            </p>

            {/* Glowing Social Media Links */}
            <div className="pt-2">
              <h5 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Connect With Us</h5>
              <div className="flex items-center gap-2.5">
                <a
                  href="https://www.linkedin.com/company/mp-x-globalsolutions/"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="LinkedIn"
                  className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 text-gray-300 flex items-center justify-center hover:bg-blue-600 hover:text-white hover:border-blue-500 hover:shadow-lg hover:shadow-blue-500/30 hover:-translate-y-1 transition-all duration-300"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/></svg>
                </a>
              </div>
            </div>
          </div>

          {/* Services Column */}
          <div className="space-y-5">
            <h4 className="text-base font-bold text-white flex items-center gap-2">
              <span className="w-1.5 h-4 bg-blue-500 rounded-full"></span>
              Services & Solutions
            </h4>
            <ul className="space-y-2.5">
              {[
                { label: 'Remote Lab Hypervisors', id: 'lab' },
                { label: 'PSCAD & MATLAB Hosting', id: 'lab' },
                { label: 'Energy Sourcing', id: 'manpower-sourcing' },
                { label: 'FPGA Remote Flashing', id: 'demolab' },
                { label: 'Digital Transformation', id: 'solutions' },
                { label: 'Custom Enterprise Web & AI', id: 'solutions' }
              ].map((item) => (
                <li key={item.label}>
                  <button
                    onClick={() => handleLinkClick(item.id)}
                    className="text-sm text-gray-400 hover:text-blue-400 transition-colors duration-200 text-left flex items-center gap-1.5 group"
                  >
                    <ArrowRight size={12} className="text-gray-600 group-hover:text-blue-400 transition-colors shrink-0" />
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Extended Resources & Support Column */}
          <div className="space-y-5">
            <h4 className="text-base font-bold text-white flex items-center gap-2">
              <span className="w-1.5 h-4 bg-cyan-400 rounded-full"></span>
              Resources & Support
            </h4>
            <ul className="space-y-2.5">
              {[
                { label: 'Platform Architecture & Specs', id: 'specs', icon: ShieldCheck },
                { label: 'Hardware Setup Guide', id: 'setupguide', icon: BookOpen },
                { label: 'Frequently Asked Questions', id: 'faq', icon: HelpCircle },
                { label: 'Help Center & Docs', id: 'helpcenter', icon: ShieldCheck },
                { label: 'Troubleshooting Tool', id: 'troubleshoot', icon: Wrench },
                { label: 'Interactive Sandbox Demo', id: 'demolab', icon: Play }
              ].map((item) => (
                <li key={item.label}>
                  <button
                    onClick={() => handleLinkClick(item.id)}
                    className="text-sm text-gray-400 hover:text-cyan-400 transition-colors duration-200 text-left flex items-center gap-2 group"
                  >
                    <item.icon size={14} className="text-gray-500 group-hover:text-cyan-400 transition-colors shrink-0" />
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Us Column */}
          <div className="space-y-5">
            <h4 className="text-base font-bold text-white flex items-center gap-2">
              <span className="w-1.5 h-4 bg-emerald-400 rounded-full"></span>
              Contact Info
            </h4>
            <div className="space-y-3.5 text-sm text-gray-400">
              <div className="flex items-start gap-3">
                <MapPin size={16} className="text-blue-400 mt-1 shrink-0" />
                <span>Theni, Tamil Nadu, India - 625531</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={16} className="text-blue-400 shrink-0" />
                <a href="mailto:info@mp-xglobalsolutions.com" className="hover:text-white transition-colors break-all">
                  info@mp-xglobalsolutions.com
                </a>
              </div>
              <div className="pt-2">
                <button
                  onClick={() => handleLinkClick('contact')}
                  className="w-full py-2 px-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-xs font-bold text-gray-200 transition-all text-center"
                >
                  Get Technical Support
                </button>
              </div>
            </div>
          </div>
        </div>



        {/* ─── BOTTOM COPYRIGHT & BACK TO TOP BUTTON ─── */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs sm:text-sm text-gray-500">
            © {currentYear} <span className="text-gray-300 font-semibold notranslate" translate="no">MP-X Global Solutions</span>. All rights reserved.
          </p>

          <div className="flex items-center gap-6 text-xs text-gray-500">
            <button onClick={() => handleLinkClick('contact')} className="hover:text-gray-300 transition-colors">Privacy Policy</button>
            <button onClick={() => handleLinkClick('contact')} className="hover:text-gray-300 transition-colors">Terms of Service</button>
            <button onClick={() => handleLinkClick('faq')} className="hover:text-gray-300 transition-colors">Security Overview</button>
          </div>

          {/* Animated Glowing Back to Top Button */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            title="Back to Top"
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-600/80 hover:bg-blue-500 text-white text-xs font-bold shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 hover:-translate-y-1 transition-all duration-300 group"
          >
            Top <ArrowUp size={14} className="group-hover:-translate-y-0.5 transition-transform" />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
